/**
 * @fileoverview Event Plan Badge component.
 * @summary Displays the plan type badge for an event (FREE, ONE_SHOT, PREMIUM).
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/components/EventPlanBadge
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
// import { faStar } from '@fortawesome/free-solid-svg-icons'
import { SubscriptionType } from '@karaonow/shared'
import type { FC } from 'react'

type PlanType = SubscriptionType.FREE | SubscriptionType.ONE_SHOT | 'subscription'
type Size = 'sm' | 'md'

interface EventPlanBadgeProps {
  planType?: PlanType
  size?: Size
  className?: string
}

/**
 * @summary Displays a badge indicating the event's plan type.
 * @param {EventPlanBadgeProps} props - Component props
 * @returns {ReactElement} Event plan badge component
 */
const EventPlanBadge: FC<EventPlanBadgeProps> = ({ planType = SubscriptionType.FREE, size = 'md', className = '' }) => {
  const { t } = useTranslation()

  // Don't show badge for free events
  if (!planType || planType === SubscriptionType.FREE) {
    return null
  }

  // Size classes
  const sizeClasses = size === 'sm' 
    ? 'px-2 py-0.5 text-[10px] gap-1' 
    : 'px-2.5 py-1 text-xs gap-1.5'
  const iconSize = size === 'sm' ? 'w-2.5 h-2.5' : 'w-3 h-3'

  const getBadgeConfig = () => {
    switch (planType) {
      case SubscriptionType.ONE_SHOT:
        return {
          label: '🎯',
          tooltip: t('subscription.oneShotTooltip') || 'Souscription événement unique',
          icon: null,
          bgColor: 'bg-indigo-100 dark:bg-indigo-900/30',
          textColor: 'text-indigo-800 dark:text-indigo-200',
          borderColor: 'border-indigo-300 dark:border-indigo-700',
        }
      case 'subscription':
        return {
          label: '⭐',
          tooltip: t('events.planSubscriptionTooltip') || 'Événement inclus dans votre abonnement',
          icon: null,
          bgColor: 'bg-blue-100 dark:bg-blue-900/30',
          textColor: 'text-blue-800 dark:text-blue-200',
          borderColor: 'border-blue-300 dark:border-blue-700',
        }
      default:
        return null
    }
  }

  const config = getBadgeConfig()
  if (!config) return null

  return (
    <span
      className={`flex-inline p-4 cursor-default items-center rounded-full border font-semibold ${sizeClasses} ${config.bgColor} ${config.textColor} ${config.borderColor} ${className}`}
      title={config.tooltip || undefined}
    >
      {config.icon && <FontAwesomeIcon icon={config.icon} className={iconSize} />}
      {config.label}
    </span>
  )
}

export default EventPlanBadge

